import { useState, useEffect, useCallback } from 'react';
import type {
  ReservationSlotOverride,
} from '../../../../domain/reservationSlots';
import type { ReservationSlotSettingsRepository } from '../../../../application/reservationSlots/ports/ReservationSlotSettingsRepository';
import {
  getSettings,
  updateSettings,
} from '../../../../application/reservationSlots';

/**
 * useReservationSlotSettings フックの戻り値型
 */
export interface UseReservationSlotSettingsReturn {
  defaultTemplateId: string | null;
  overrides: ReservationSlotOverride[];
  isLoading: boolean;
  saving: boolean;
  error: string | null;
  successMessage: string | null;
  reload: () => Promise<void>;
  setDefaultTemplate: (templateId: string | null) => void;
  addOverride: (doctorId: number, doctorName: string, templateId: string) => void;
  updateOverrideTemplate: (doctorId: number, templateId: string) => void;
  replaceOverrideDoctor: (
    currentDoctorId: number,
    nextDoctorId: number,
    nextDoctorName: string
  ) => void;
  removeOverride: (doctorId: number) => void;
  save: () => Promise<boolean>;
  clearError: () => void;
  clearSuccess: () => void;
}

/**
 * 基本設定タブ用カスタムフック
 *
 * @description
 * デフォルトテンプレートと医師個別の上書き設定を管理する。
 * overrides はローカルで編集し、保存時に全置換でAPIへ送信する。
 *
 * @param {ReservationSlotSettingsRepository} repository - DIされるRepository実装
 */
export const useReservationSlotSettings = (
  repository: ReservationSlotSettingsRepository
): UseReservationSlotSettingsReturn => {
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<ReservationSlotOverride[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const settings = await getSettings(repository);
      setDefaultTemplateId(settings.defaultTemplateId);
      setOverrides(settings.overrides);
    } catch (e) {
      setError(e instanceof Error ? e.message : '基本設定の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDefaultTemplate = useCallback((templateId: string | null) => {
    setDefaultTemplateId(templateId);
  }, []);

  const addOverride = useCallback(
    (doctorId: number, doctorName: string, templateId: string) => {
      setOverrides((prev) => {
        if (prev.some((o) => o.doctorId === doctorId)) {
          return prev;
        }
        return [...prev, { doctorId, doctorName, templateId }];
      });
    },
    []
  );

  const updateOverrideTemplate = useCallback((doctorId: number, templateId: string) => {
    setOverrides((prev) =>
      prev.map((o) => (o.doctorId === doctorId ? { ...o, templateId } : o))
    );
  }, []);

  /**
   * 上書き行の対象医師を差し替える
   *
   * @description
   * 上書き一覧を行単位で編集できるようにするためのもの。
   * 差し替え先の医師が既に別行に登録されている場合は重複を避けて何もしない。
   * 行の並び順とテンプレート選択は維持する。
   */
  const replaceOverrideDoctor = useCallback(
    (currentDoctorId: number, nextDoctorId: number, nextDoctorName: string) => {
      setOverrides((prev) => {
        if (currentDoctorId === nextDoctorId) {
          return prev;
        }
        if (prev.some((o) => o.doctorId === nextDoctorId)) {
          return prev;
        }
        return prev.map((o) =>
          o.doctorId === currentDoctorId
            ? { ...o, doctorId: nextDoctorId, doctorName: nextDoctorName }
            : o
        );
      });
    },
    []
  );

  const removeOverride = useCallback((doctorId: number) => {
    setOverrides((prev) => prev.filter((o) => o.doctorId !== doctorId));
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await updateSettings(repository, {
        defaultTemplateId,
        overrides: overrides.map((o) => ({
          doctorId: o.doctorId,
          templateId: o.templateId,
        })),
      });
      setDefaultTemplateId(result.defaultTemplateId);
      setOverrides(result.overrides);
      setSuccessMessage('基本設定を保存しました');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : '基本設定の保存に失敗しました');
      return false;
    } finally {
      setSaving(false);
    }
  }, [repository, defaultTemplateId, overrides]);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccessMessage(null), []);

  return {
    defaultTemplateId,
    overrides,
    isLoading,
    saving,
    error,
    successMessage,
    reload,
    setDefaultTemplate,
    addOverride,
    updateOverrideTemplate,
    replaceOverrideDoctor,
    removeOverride,
    save,
    clearError,
    clearSuccess,
  };
};
